// HotelListingForm.js
import React, { useState, useEffect } from "react";
import { FaUpload } from "react-icons/fa";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./HotelListingForm.css";
// Services
import { createHotelListing, checkListingLimit, sendLimitIncreaseRequest, getAmenities, createAmenity } from "../services/hotelService";
import { getAgentDetails } from "../services/agentService";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
// Location Picker
import LocationPicker from "../components/LocationPicker";
// Toast
import { toast } from 'react-toastify';

function HotelListingForm() {
  const BACKEND_URL = process.env.REACT_APP_API_URL;
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const loggedUserId = user?.id;
  const loggedUserRole = user?.role_id;
  const navigate = useNavigate();

  // URL param: ownerId only exists when agent creates listing for a specific owner
  const { ownerId } = useParams();

  const [hotelName, setHotelName] = useState("");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [coordinates, setCoordinates] = useState("");
  const [roomType, setRoomType] = useState("");
  const [roomPrice, setRoomPrice] = useState("");
  const [maxGuest, setMaxGuest] = useState("");
  const [images, setImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [discounts, setDiscounts] = useState("");
  const [coinRewards, setCoinRewards] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [roomTypesList, setRoomTypesList] = useState([]);
  const [hasReachedLimit, setHasReachedLimit] = useState(false);
  const [currentLimitMessage, setCurrentLimitMessage] = useState("");
  const [showRequestModal, setShowRequestModal] = useState(false);

  const [kokoSelected, setKokoSelected] = useState(false);
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [newAmenity, setNewAmenity] = useState("");
  const [amenityError, setAmenityError] = useState("");

  const [isAgent, setIsAgent] = useState(false);
  const [agentDetailsId, setAgentDetailsId] = useState(null);
  const [isOwnerApproved, setIsOwnerApproved] = useState(true);
  const [ownerCheckLoading, setOwnerCheckLoading] = useState(true);

  // Check owner approval status
  useEffect(() => {
    async function verifyOwnerApproval() {
      const targetUserId = ownerId || loggedUserId;
      if (!targetUserId) {
        setOwnerCheckLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem("jwt_token");
        const res = await axios.get(`${BACKEND_URL}/users/${targetUserId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const targetUser = res.data;
        const approved =
          targetUser?.status_id === 4 ||
          targetUser?.status_id === 5 ||
          targetUser?.Status?.name === "Approved" ||
          targetUser?.Status?.name === "Active";
        setIsOwnerApproved(approved);
      } catch (err) {
        const approved = user?.status_id === 4 || user?.status_id === 5;
        setIsOwnerApproved(approved);
      } finally {
        setOwnerCheckLoading(false);
      }
    }
    verifyOwnerApproval();
  }, [ownerId, loggedUserId, BACKEND_URL, user?.status_id]);

  useEffect(() => {
    async function checkLimit() {
      let userIdForLimit;
      if (ownerId) {
        userIdForLimit = ownerId;
      } else {
        userIdForLimit = loggedUserId;
      }

      if (!userIdForLimit) return;

      try {
        const result = await checkListingLimit(userIdForLimit);
        setHasReachedLimit(false);
        setCurrentLimitMessage(result.message);
      } catch (err) {
        setHasReachedLimit(true);
        setCurrentLimitMessage(err.error || "Limit reached.");
      }
    }

    checkLimit();
  }, [loggedUserId, ownerId]);

  // Detect if logged user is an approved agent
  useEffect(() => {
    async function checkAgent() {
      try {
        const agent = await getAgentDetails();
        if (agent && agent.status_id === 4) {
          setIsAgent(true);
          setAgentDetailsId(agent.id);
        } else {
          setIsAgent(false);
          setAgentDetailsId(null);
        }
      } catch (e) {
        setIsAgent(false);
        setAgentDetailsId(null);
      }
    }
    checkAgent();
  }, []);

  useEffect(() => {
    // Fetch room types
    axios
      .get(`${BACKEND_URL}/hotels/room-types`)
      .then((res) => {
        setRoomTypesList(res.data);
      })
      .catch((err) => console.error("Error fetching room types:", err));
  }, [BACKEND_URL]);

  useEffect(() => {
    async function loadAmenities() {
      try {
        const data = await getAmenities();
        setAmenitiesList(data);
      } catch (err) {
        console.error("Failed to load amenities", err);
      }
    }
    loadAmenities();
  }, []);

  const toggleAmenity = (id) => {
    setSelectedAmenities((prev) =>
      prev.includes(id)
        ? prev.filter((a) => a !== id)
        : [...prev, id]
    );
  };

  const handleAddAmenity = async () => {
    if (!newAmenity.trim()) {
      setAmenityError("Amenity name is required");
      return;
    }

    try {
      const created = await createAmenity(newAmenity.trim());
      setAmenitiesList((prev) => [...prev, created]);
      setSelectedAmenities((prev) => [...prev, created.id]);
      setNewAmenity("");
      setAmenityError("");
      toast.success("Amenity added!");
    } catch (err) {
      setAmenityError(err.response?.data?.error || "Failed to add amenity");
      toast.error("Failed to add amenity");
    }
  };

  // Handle image preview and files
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    // Validate file types/sizes here if needed
    const validFiles = files.filter(file => file.type.startsWith('image/') || file.type === 'application/pdf');

    if (validFiles.length !== files.length) {
      toast.warning("Some files were ignored. Only images and PDFs are allowed.");
    }

    const fileURLs = validFiles.map((file) => URL.createObjectURL(file));
    setImages((prev) => [...prev, ...fileURLs]);
    setImageFiles((prev) => [...prev, ...validFiles]);
  };

  // Handle availability dates
  const handleDateChange = (date) => {
    const dateStr = date.toDateString();
    setAvailableDates((prev) => {
      if (prev.some((d) => d.toDateString() === dateStr)) {
        return prev.filter((d) => d.toDateString() !== dateStr);
      } else {
        return [...prev, date];
      }
    });
  };

  const tileClassName = ({ date, view }) => {
    if (
      view === "month" &&
      availableDates.some((d) => d.toDateString() === date.toDateString())
    ) {
      return "highlight-date";
    }
    return null;
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!agreeTerms) {
      toast.error("Please agree to the terms and verification!");
      return;
    }

    const isLoggedUserAgent = loggedUserRole === 6 || loggedUserRole === 4 || user?.Role?.name === "Traveler Agent" || user?.Role?.name === "Agent";

    let userIdForListing;

    if (ownerId) {
      userIdForListing = ownerId;
    } else {
      if (isLoggedUserAgent) {
        toast.error("As an agent, you must select a hotel owner to create a listing.");
        return;
      }
      if (!loggedUserId) {
        toast.error("User not logged in.");
        return;
      }
      userIdForListing = loggedUserId;
    }

    // Check limit on submit
    try {
      await checkListingLimit(userIdForListing);
    } catch (err) {
      toast.error(err.error || "You have reached the listing limit.");
      return;
    }

    let paymentMethodIds = [];
    if (kokoSelected) paymentMethodIds.push(4);

    if (!isAgent && !isOwnerApproved) {
      toast.error("Your Hotel Owner account is pending approval by an administrator. You cannot create listings until approved.");
      return;
    }

    const listingData = {
      userId: userIdForListing,
      traveler_agent_details_id: isAgent ? agentDetailsId : null,
      name: hotelName,
      description,
      address,
      coordinates,
      currencyId: 4,
      roomTypeId: roomType,
      price: roomPrice,
      maxGuest,
      amenities: selectedAmenities,
      discounts,
      coinRewards,
      availableDates: availableDates.map((d) => d.toISOString()),
      payment_method_ids: paymentMethodIds,
    };

    try {
      const response = await createHotelListing(listingData, imageFiles);
      toast.success(response.message || "Listing created successfully!");

      // Reset form
      setHotelName("");
      setDescription("");
      setAddress("");
      setCoordinates("");
      setRoomType("");
      setRoomPrice("");
      setMaxGuest("");
      setImages([]);
      setImageFiles([]);
      setAvailableDates([]);
      setDiscounts("");
      setCoinRewards("");
      setAgreeTerms(false);

      // Redirect to profile page after 2 seconds (so they can see the beautiful toast!)
      setTimeout(() => {
        if (loggedUserId) {
          navigate(`/profile/${loggedUserId}`);
        } else {
          navigate(-1);
        }
      }, 2000);
    } catch (err) {
      console.error("Error creating hotel listing:", err);
      toast.error("Failed to create listing: " + (err.response?.data?.error || err.message));
    }
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Helper for increasing limit
  const handleLimitRequest = async () => {
    const username = document.getElementById("reqUsername").value;
    const role = document.getElementById("reqRole").value;
    const email = document.getElementById("reqEmail").value;
    const request = document.getElementById("reqReason").value;

    if (!username || !role || !email || !request) {
      toast.error("All fields are required!");
      return;
    }

    try {
      const res = await sendLimitIncreaseRequest(loggedUserId, { username, role, email, request });
      toast.success(res.message);
      setShowRequestModal(false);
    } catch (error) {
      toast.error(error.error || "Failed to send request.");
    }
  };

  if (!ownerCheckLoading && !isOwnerApproved && !isAgent) {
    return (
      <div className="hotel-listing-container">
        <h2 className="booking-title">List Your Hotel</h2>
        <div style={{ textAlign: "center", padding: "50px 20px", background: "white", borderRadius: "16px", boxShadow: "0 10px 30px rgba(0,0,0,0.08)", margin: "20px auto", maxWidth: "600px" }}>
          <div style={{ fontSize: "56px", marginBottom: "15px" }}>⏳</div>
          <h3 style={{ fontSize: "22px", fontWeight: "700", marginBottom: "12px", color: "#1f2937" }}>
            Account Pending Admin Approval
          </h3>
          <p style={{ color: "#6b7280", lineHeight: "1.6", fontSize: "15px", marginBottom: "25px" }}>
            Your Hotel Owner registration is currently under review by our administration team. You will be able to create listings once your account has been approved.
          </p>
          <button 
            type="button"
            className="submit-btn" 
            style={{ maxWidth: "200px", margin: "0 auto", padding: "12px 24px", cursor: "pointer" }} 
            onClick={() => navigate(loggedUserId ? `/profile/${loggedUserId}` : "/travelerhome")}
          >
            Back to Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hotel-listing-container">
      <h2 className="booking-title">List Your Hotel</h2>
      <div className="limit-info-banner" style={{ textAlign: 'center', marginBottom: '20px', color: '#555', fontWeight: 'bold', fontSize: '16px' }}>
        {currentLimitMessage}
      </div>

      {/* Steps Indicator could go here if multi-step is implemented later */}

      <form className="hotel-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Listing Name</label>
          <input
            type="text"
            value={hotelName}
            onChange={(e) => setHotelName(e.target.value)}
            placeholder="Enter Listing Name"
            required
            className="input-field"
          />
        </div>

        <div className="form-group">
          <label>Hotel / Property Address</label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="e.g. 123 Galle Road, Colombo 03"
            required
            className="input-field"
          />
        </div>

        <LocationPicker
          value={coordinates}
          onChange={setCoordinates}
          address={address}
          onAddressChange={setAddress}
          required={true}
          label="Google Maps Location or Coordinates"
          helpText="Pick your exact hotel location on the interactive map or enter coordinates / Google Maps link manually."
        />

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Write a short description"
            required
            className="input-field"
          ></textarea>
        </div>

        <div className="room-row">
          <div className="form-group">
            <label>Room Type</label>
            <select
              value={roomType}
              onChange={(e) => setRoomType(e.target.value)}
              required
              className="input-field"
            >
              <option value="">Select Room Type</option>
              {roomTypesList.map((rt) => (
                <option key={rt.id} value={rt.id}>
                  {rt.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Room Price</label>
            <input
              type="number"
              value={roomPrice}
              onChange={(e) => setRoomPrice(e.target.value)}
              placeholder="Enter Room Price"
              required
              min="0"
              className="input-field"
            />
          </div>

          <div className="form-group">
            <label>Maximum Guests</label>
            <input
              type="number"
              value={maxGuest}
              onChange={(e) => setMaxGuest(e.target.value)}
              placeholder="Enter Guests"
              required
              min="1"
              className="input-field"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Property Images</label>
          <div className="upload-box">
            <FaUpload className="upload-icon" />
            <p>Drag & drop or click to upload (JPG, PNG)</p>
            <input
              type="file"
              multiple
              accept="image/*,.pdf"
              onChange={handleImageUpload}
            />
          </div>
        </div>

        <div className="image-preview">
          {images.map((src, index) => (
            <div key={index} className="image-preview-item">
              <img src={src} alt="hotel upload" />
              <button
                type="button"
                className="remove-image-btn"
                onClick={() => handleRemoveImage(index)}
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <h4>Amenities</h4>
        <div className="amenities-section">
          <div className="amenities-list">
            {amenitiesList.map((amenity) => (
              <label key={amenity.id} className="amenity-item">
                <input
                  type="checkbox"
                  checked={selectedAmenities.includes(amenity.id)}
                  onChange={() => toggleAmenity(amenity.id)}
                />
                {amenity.name}
              </label>
            ))}
          </div>

          <div className="add-amenity-box">
            <input
              type="text"
              placeholder="Add new amenity..."
              value={newAmenity}
              onChange={(e) => setNewAmenity(e.target.value)}
            />
            <button type="button" onClick={handleAddAmenity}>Add</button>
          </div>
          {amenityError && <p className="error-text">{amenityError}</p>}
        </div>

        <h4>Availability</h4>
        <Calendar onClickDay={handleDateChange} tileClassName={tileClassName} className="react-calendar" />

        <h4>Offers & Rewards</h4>
        <div className="room-row">
          <div className="form-group">
            <label>Discounts (%)</label>
            <input
              type="number"
              value={discounts}
              onChange={(e) => setDiscounts(e.target.value)}
              placeholder="e.g. 10"
              min="0"
              max="100"
            />
          </div>
          <div className="form-group">
            <label>Coin Rewards</label>
            <input
              type="number"
              value={coinRewards}
              onChange={(e) => setCoinRewards(e.target.value)}
              placeholder="e.g. 50"
              min="0"
            />
          </div>
        </div>

        <h4>Payment Methods (Optional)</h4>
        <div className="pm-container">

          <label className="pm-option">
            <input
              type="checkbox"
              checked={kokoSelected}
              onChange={(e) => setKokoSelected(e.target.checked)}
            />
            <span className="pm-label">KoKo Pay</span>
          </label>
        </div>

        <div className="terms-checkbox">
          <label>
            <input
              type="checkbox"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
              required
            />
            I agree to the terms and verification
          </label>
        </div>

        <button type="submit" className="submit-btn">
          List Hotel
        </button>


      </form>

      {/* MODAL */}
      {showRequestModal && (
        <div className="rml-modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="rml-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="rml-modal-title">Increase Listing Limit</h2>
            <p className="rml-modal-subtitle">
              Submit your request to increase your allowable listing count.
            </p>

            <input type="text" id="reqUsername" placeholder="Your Name" className="rml-input" />
            <input type="text" id="reqRole" placeholder="Role (Owner/Agent)" className="rml-input" />
            <input type="email" id="reqEmail" placeholder="Email" className="rml-input" />
            <textarea id="reqReason" placeholder="Reason for request..." className="rml-textarea"></textarea>

            <div className="rml-modal-actions">
              <button className="rml-cancel" onClick={() => setShowRequestModal(false)}>
                Cancel
              </button>
              <button className="rml-submit" onClick={handleLimitRequest}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HotelListingForm;
